import React, { useState, useEffect } from "react";
import { Select, Button, Box, Input, Modal } from "@sainsburys-tech/fable";
import Headerfile from "../../common/Headerfile";
import Footerfile from "../../common/Footerfile";
import Styles from "./EditPrinter.module.css";
import { useNavigate, useParams } from "react-router-dom";
import { alpha, styled } from "@mui/material/styles";
import { pink } from "@mui/material/colors";
import Switch from "@mui/material/Switch";
import { useGetPostsQuery, usePutDataMutation } from "../../../api/apiConfig";
import { getLogisticsRole } from "../../../utils/authToken";
import { USER_ROLE } from "../../../constants/roles";
import Unauthorized from "../../unauthorized/Unauthorized";

const EditPrinter = () => {
  interface IState {
    Id?: string;
    IsRetired?: boolean;
    Status?: string;
    Configuration?: {
      IP?: string;
      IsPublished?: boolean;
      IsShared?: boolean;
      IsQueuingEnabled: boolean;
      Port?: number;
      Type?: string;
      LabelMediaType?: string;
    };
    Identity?: {
      Name?: string;
      SBPrinterId?: string;
      SerialNumber?: string;
      Location?: {
        Id?: string;
        Name?: string;
        WarehouseFloor?: string;
        WarehouseLocality?: string;
      };
      PrinterModel: {
        Id?: string;
        Name?: string;
      };
    };
    StatusOverride?: {
      IsAllowed?: boolean;
      PreventTill?: null;
      StatusCode?: string;
      StatusDescription?: string;
      LastUpdate?: string | Date;
    };
  }

  const label = { inputProps: { "aria-label": "Color switch demo" } };
  const { Id } = useParams();
  const {
    isSuccess,
    data: getData,
    refetch
  } = useGetPostsQuery(`printers/${Id}`);

  const [location, setLocation] = useState<Location[]>([]);
  const [printerModel, setPrinterModel] = useState<PrinterModel[]>([]);
  const [apiHeading, setApiHeading] = useState<string | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);

  interface Location {
    LocationId: string;
    Name: string;
  }

  interface PrinterModel {
    Id: string;
    Name: string;
    PrinterModelId?: string;
  }
  const {
    isLoading: isLocationLoading,
    isSuccess: isLocationSuccess,
    data: getAllLocations,
    refetch: fetchAllLocations
  } = useGetPostsQuery("locations/GetLocationFromIdentityToken");

  const {
    isLoading: isPrinterModelLoading,
    isSuccess: isPrinterModelSuccess,
    data: printerModelList
  } = useGetPostsQuery("printer-models");

  useEffect(() => {
    if (isLocationSuccess && getAllLocations?.Model) {
      const validLocations = getAllLocations.Model.filter(
        (location: any): location is Location =>
          location.LocationId &&
          location.Name &&
          location.Status
      );

      const sortedLocations: Location[] = validLocations.sort(
        (a: any, b: any) => a.Name.localeCompare(b.Name)
      );
      setLocation(sortedLocations);
    }
    if (isPrinterModelSuccess && printerModelList?.Model) {
      const sortedPrinterModels = [
        ...(printerModelList.Model as PrinterModel[])
      ].sort((a, b) => a.Name.localeCompare(b.Name));
      setPrinterModel(sortedPrinterModels);
    }
  }, [isLocationSuccess, isPrinterModelSuccess]);

  useEffect(() => {
    refetch();
    fetchAllLocations();
  }, []);

  const [putData] = usePutDataMutation();

  const [values, setValues] = useState<IState>({
    Id: "",
    IsRetired: false,
    Status: "I",
    Configuration: {
      IP: "",
      IsPublished: false,
      IsShared: false,
      IsQueuingEnabled: false,
      Port: 0,
      Type: "",
      LabelMediaType: ""
    },
    Identity: {
      Name: "",
      SBPrinterId: "",
      SerialNumber: "",
      Location: {
        Id: "",
        Name: "default",
        WarehouseFloor: "",
        WarehouseLocality: ""
      },
      PrinterModel: {
        Id: "",
        Name: ""
      }
    },
    StatusOverride: {
      IsAllowed: true,
      PreventTill: null,
      StatusCode: "",
      StatusDescription: "",
      LastUpdate: ""
    }
  });
  interface IErrors {
    Id?: string;
    IsRetired?: string;
    Status?: string;
    Configuration?: {
      IP?: string;
      IsPublished?: string;
      IsShared?: string;
      Port?: string;
      Type?: string;
      LabelMediaType?: string;
    };
    Identity?: {
      Name?: string;
      SBPrinterId?: string;
      SerialNumber?: string;
      Location?: {
        Id?: string;
        Name?: string;
        WarehouseFloor?: string;
        WarehouseLocality?: string;
      };
      PrinterModel?: {
        Id?: string;
        Name?: string;
      };
    };
    StatusOverride?: {
      IsAllowed?: boolean;
      PreventTill?: null;
      StatusCode?: string;
      StatusDescription?: string;
      LastUpdate?: string | Date;
    };
  }
  const initialState: IState = {
    IsRetired: false,
    Status: "I",
    Configuration: {
      IP: "",
      IsPublished: false,
      IsShared: false,
      IsQueuingEnabled: false,
      Port: 9100,
      Type: "",
      LabelMediaType: ""
    },
    Identity: {
      Name: "",
      SBPrinterId: "",
      SerialNumber: "",
      Location: {
        Id: "",
        Name: "default",
        WarehouseFloor: "",
        WarehouseLocality: ""
      },
      PrinterModel: {
        Id: "",
        Name: ""
      }
    },
    StatusOverride: {
      IsAllowed: true,
      PreventTill: null,
      StatusCode: "",
      StatusDescription: "",
      LastUpdate: ""
    }
  };
  const [errors, setErrors] = useState<IErrors>({});

  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();

  const loggedUserRole = getLogisticsRole();

  const isDisabled =
    loggedUserRole?.includes(USER_ROLE.ADMIN) ||
      loggedUserRole?.includes(USER_ROLE.READ_WRITE)
      ? false
      : true;

  useEffect(() => {
    if (isSuccess && getData?.Model) {
      const newData: IState = getData.Model as IState;
      setValues({
        Id: newData?.Id || "",
        IsRetired: newData?.IsRetired || false,
        Status: newData?.Status || "",
        Configuration: {
          IP: newData?.Configuration?.IP || "",
          IsPublished: newData?.Configuration?.IsPublished || false,
          IsShared: newData?.Configuration?.IsShared || false,
          IsQueuingEnabled:
            newData?.Configuration?.IsQueuingEnabled || false,
          Port: newData?.Configuration?.Port || 0,
          Type: newData?.Configuration?.Type || "",
          LabelMediaType: newData?.Configuration?.LabelMediaType || ""
        },
        Identity: {
          Name: newData?.Identity?.Name || "",
          SBPrinterId: newData?.Identity?.SBPrinterId || "",
          SerialNumber: newData?.Identity?.SerialNumber || "",
          Location: {
            Id: newData?.Identity?.Location?.Id || "",
            Name: newData?.Identity?.Location?.Name || "",
            WarehouseFloor: newData?.Identity?.Location?.WarehouseFloor
              ? newData?.Identity?.Location?.WarehouseFloor
              : "-",
            WarehouseLocality:
              newData?.Identity?.Location?.WarehouseLocality || ""
          },
          PrinterModel: {
            Id: newData?.Identity?.PrinterModel?.Id || "",
            Name: newData?.Identity?.PrinterModel?.Name || ""
          }
        },

        StatusOverride: {
          IsAllowed: newData?.StatusOverride?.IsAllowed || false,
          PreventTill: newData?.StatusOverride?.PreventTill || null,
          StatusCode: newData?.StatusOverride?.StatusCode || "",
          StatusDescription: newData?.StatusOverride?.StatusDescription || "",
          LastUpdate: newData?.StatusOverride?.LastUpdate || ""
        }
      });
    }
  }, [isSuccess, getData]);

  const Android12Switch = styled(Switch)(({ theme }) => ({
    padding: 8,
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "#f06c00",
      "&:hover": {
        backgroundColor: alpha(pink[600], theme.palette.action.hoverOpacity)
      }
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#84776c"
    },
    "& .MuiSwitch-track": {
      borderRadius: 22 / 2,
      "&::before, &::after": {
        content: '""',
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        width: 16,
        height: 16
      },
      "&::before": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
          theme.palette.getContrastText(theme.palette.primary.main)
        )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
        left: 12
      },
      "&::after": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
          theme.palette.getContrastText(theme.palette.primary.main)
        )}" d="M19,13H5V11H19V13Z" /></svg>')`,
        right: 12
      }
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "none",
      width: 16,
      height: 16,
      margin: 2
    }
  }));

  const handleChange = (name: string, value: string | boolean | File) => {
    const keys = name.split(".");
    if (keys.length === 1) {
      setValues({ ...values, [name]: value });
    } else {
      setValues((prevValues) => {
        let updatedValues: any = { ...prevValues };
        let current: any = updatedValues;
        for (let i = 0; i < keys.length - 1; i++) {
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        return updatedValues;
      });
    }
  };

  const handleToggleChange = (name: string) => (event: any) => {
    let value;
    if (name === "Status") {
      value = event.target.checked ? "A" : "I";
    } else {
      value = event.target.checked;
    }
    handleChange(name, value);
  };

  const handlePrinterModelChange = (value: string) => {
    const selectedOption = printerModel.find((model) => model.Name === value);
    handleChange("Identity.PrinterModel.Name", value);
    handleChange(
      "Identity.PrinterModel.Id",
      selectedOption?.PrinterModelId || selectedOption?.Id || ""
    );
  };

  interface IData {
    StatusCode: number;
    Status: string;
    TotalRecords?: number | null;
    Message?: string;
    Model?: any;
    Value?: string;
  }

  interface IResponse {
    data: IData;
    TotalRecords?: number | null;
  }

  function isIResponse(response: any): response is IResponse {
    return response.data !== undefined && typeof response.data === "object";
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    const validationErrors = validate(values);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      setIsSaveButtonDisabled(true);
      try {
        const lastUpdateString = values?.StatusOverride?.LastUpdate;
        const lastUpdateDate = lastUpdateString
          ? new Date(lastUpdateString)
          : new Date();

        if (values?.Identity) {
          values.Identity.SBPrinterId = values.Identity.Name;
        }
        const response = await putData({
          Method: "printers",
          ...values,
          LastUpdate: lastUpdateDate
        });
        if (
          isIResponse(response) &&
          response.data.StatusCode === 0 &&
          response.data.Status === "Ok"
        ) {
          setIsSaveButtonDisabled(false);
          setApiMessage("Printer updated successfully" || null);
          setApiHeading("Success!!!");
          setIsOpen(true);
          setValues(initialState);
          setTimeout(() => {
            navigate("/addprinter");
          }, 1500);
        } else if (isIResponse(response) && response.data) {
          setIsSaveButtonDisabled(false);
          setApiMessage(response.data.Message || null);
          setApiHeading("Error!!!");
          setIsOpen(true);
          console.error("Error response:", response);
        } else {
          setIsSaveButtonDisabled(false);
          console.error("Something Went Wrong..", response);
        }
      } catch (error) {
        setIsSaveButtonDisabled(false);
        console.error(error);
      }
    }
  };

  const validate = (values: IState) => {
    let errors: any = {};
    const ipRegex =
      /^((25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)$/;

    if (!values?.Identity?.Name) {
      errors.Identity = {
        ...errors.Identity,
        Name: "Printer Name is required"
      };
    }

    if (
      !values?.Identity?.Location?.Name ||
      values?.Identity?.Location?.Name === "default"
    ) {
      errors.Identity = {
        ...errors.Identity,
        Location: { ...errors.Identity.Location, Name: "Location is required" }
      };
    }

    if (
      !values?.Identity?.PrinterModel.Name ||
      values?.Identity?.PrinterModel.Name === "default"
    ) {
      errors.Identity = {
        ...errors.Identity,
        PrinterModel: {
          ...errors.Identity.PrinterModel,
          Name: "Printer Model is required"
        }
      };
    }

    if (
      !values.Configuration?.Type ||
      values.Configuration?.Type === "default"
    ) {
      errors.Configuration = {
        ...errors.Configuration,
        Type: "Printer Type is required"
      };
    }

    if (values.Configuration?.Type === "Z") {
      if (!values.Configuration?.Port) {
        errors.Configuration = {
          ...errors.Configuration,
          Port: "Port is required"
        };
      } else if (!/^\d+$/.test(values.Configuration.Port.toString())) {
        errors.Configuration = {
          ...errors.Configuration,
          Port: "Port number must only contain digits"
        };
      } else if (
        values.Configuration.Port.toString().length < 1 ||
        values.Configuration.Port.toString().length > 5
      ) {
        errors.Configuration = {
          ...errors.Configuration,
          Port: "Port number must be between 1 to 5 digits"
        };
      }

      if (!values.Configuration.IP) {
        errors.Configuration = {
          ...errors.Configuration,
          IP: "IP Address is required"
        };
      } else if (!ipRegex.test(values.Configuration.IP)) {
        errors.Configuration = {
          ...errors.Configuration,
          IP: "IP Address is invalid"
        };
      }
    } else {
      if (!values.Configuration?.Port) {
        errors.Configuration = {
          ...errors.Configuration,
          Port: "Port is required"
        };
      }

      if (!values?.Configuration?.IP) {
        errors.Configuration = {
          ...errors.Configuration,
          IP: "IP Address is required"
        };
      }
    }

    return errors;
  };

  const handleLocationChange = (value: string) => {
    const selectedOption = location.find((loc) => loc.Name === value);
    handleChange("Identity.Location.Name", value);
    handleChange("Identity.Location.Id", selectedOption?.LocationId || "");
  };

  return (
    <div>
      <div className={Styles.container}>
        <Headerfile />
        {isDisabled ? (
          <Unauthorized />
        ) : (
          <Box as="div" className={Styles.box}>
            <form onSubmit={handleSubmit} className={Styles.formContainer}>
              <h5 className={Styles.heading}>Edit Printer</h5>
              <p className={Styles.rightAlign}>
                <span className={Styles.redAsterisk}>*</span>Indicates required
                field
              </p>{" "}
              <div className="row">
                <div
                  className={`col-12 d-flex justify-content-between align-items-center flex-wrap ${Styles.toggleContainer}`}
                >
                  <label className={Styles.label1}>
                    <b>Is Published</b>
                    <Android12Switch
                      {...label}
                      checked={values?.Configuration?.IsPublished}
                      onChange={handleToggleChange("Configuration.IsPublished")}
                    />
                  </label>
                  <br />
                  <label className={Styles.label2}>
                    <b>Is Retired</b>
                    <Android12Switch
                      {...label}
                      checked={values.IsRetired}
                      onChange={handleToggleChange("IsRetired")}
                    />
                  </label>
                  <br />
                  <label className={Styles.label3}>
                    <b>Is Shared</b>
                    <Android12Switch
                      {...label}
                      checked={values?.Configuration?.IsShared}
                      onChange={handleToggleChange("Configuration.IsShared")}
                    />
                  </label>
                  <br />
                  <label className={Styles.label4}>
                    <b>Printer Status</b>
                    <Android12Switch
                      {...label}
                      checked={values.Status === "A" ? true : false}
                      onChange={handleToggleChange("Status")}
                    />
                  </label>
                  <br />
                  <label className={Styles.label5}>
                    <b>Is Queuing Enabled</b>
                    <Android12Switch
                      {...label}
                      checked={values.Configuration?.IsQueuingEnabled ? true : false}
                      onChange={handleToggleChange("Configuration.IsQueuingEnabled")}
                    />
                  </label>
                </div>
              </div>
              <Select
                label="Location*"
                options={[
                  { label: "Select Location", value: "default" },
                  ...(isLocationLoading
                    ? [
                      {
                        label: "Loading...",
                        value: "loading",
                        isDisabled: isLocationLoading
                      }
                    ]
                    : location.map((loc) => ({
                      value: loc?.Name,
                      label: loc?.Name,
                      id: loc?.LocationId
                    })))
                ]}
                style={{ textAlign: "left" }}
                name="Identity.Location.Name"
                onChange={(value) => handleLocationChange(value)}
                value={values?.Identity?.Location?.Name}
              />
              {isSubmitted && errors.Identity?.Location?.Name && (
                <div className={Styles.error}>
                  {errors.Identity.Location.Name}
                </div>
              )}
              <br />
              <Select
                label="Printer Model*"
                options={[
                  { label: "Select Printer", value: "default" },
                  ...(isPrinterModelLoading
                    ? [
                      {
                        label: "Loading...",
                        value: "loading",
                        isDisabled: isPrinterModelLoading
                      }
                    ]
                    : (printerModel?.map((model) => ({
                      label: model?.Name,
                      value: model.Name,
                      id: model?.PrinterModelId
                    })) ?? []))
                ]}
                style={{ textAlign: "left" }}
                name="Identity.PrinterModel.Name"
                onChange={(value) => handlePrinterModelChange(value)}
                value={values?.Identity?.PrinterModel.Name}
              />
              {isSubmitted && errors.Identity?.PrinterModel?.Name && (
                <div className={Styles.error}>
                  {errors.Identity.PrinterModel.Name}
                </div>
              )}
              <br />
              <Select
                label="Printer Type*"
                options={[
                  { label: "Select Printer Type", value: "default" },
                  { label: "ZPL", value: "Z" },
                  { label: "Others", value: "O" }
                ]}
                style={{ textAlign: "left" }}
                name="Configuration.Type"
                onChange={(e) => handleChange("Configuration.Type", e)}
                value={values.Configuration?.Type}
              />
              {isSubmitted && errors.Configuration?.Type && (
                <div className={Styles.error}>{errors.Configuration.Type}</div>
              )}
              <br />
              <label className={Styles.label}>
                <Input
                  name="Identity.Name"
                  label="Printer Name*"
                  autocomplete="off"
                  type="text"
                  onChange={(e) => handleChange("Identity.Name", e)}
                  value={values?.Identity?.Name}
                />
                {isSubmitted && errors.Identity?.Name && (
                  <div className={Styles.error}>{errors.Identity.Name}</div>
                )}
              </label>
              <br />
              <label className={Styles.label}>
                <Input
                  name="Configuration.IP"
                  label={
                    values?.Configuration?.Type === "Z"
                      ? "IP Address*"
                      : "IP Address"
                  }
                  autocomplete="off"
                  type="text"
                  onChange={(e) => handleChange("Configuration.IP", e)}
                  value={values?.Configuration?.IP}
                />
                {isSubmitted && errors.Configuration?.IP && (
                  <div className={Styles.error}>{errors.Configuration.IP}</div>
                )}
              </label>
              <br />
              <label className={Styles.label}>
                <Input
                  name="Configuration.Port"
                  label="Port*"
                  autocomplete="off"
                  type="text"
                  onChange={(e) => handleChange("Configuration.Port", e)}
                  value={values?.Configuration?.Port?.toString() || ""}
                />
                {isSubmitted && errors.Configuration?.Port && (
                  <div className={Styles.error}>
                    {errors.Configuration.Port}
                  </div>
                )}
              </label>
              <br />
              <Input
                name="Identity.Location.WarehouseFloor"
                label="Warehouse"
                autocomplete="off"
                type="text"
                onChange={(e) =>
                  handleChange("Identity.Location.WarehouseFloor", e)
                }
                value={values?.Identity?.Location?.WarehouseFloor}
              />
              {isSubmitted && errors.Identity?.Location?.WarehouseFloor && (
                <div className={Styles.error}>
                  {errors.Identity.Location.WarehouseFloor}
                </div>
              )}
              <br />
              <Input
                name="Identity.Location.WarehouseLocality"
                label="Warehouse Locality"
                autocomplete="off"
                type="text"
                onChange={(e) =>
                  handleChange("Identity.Location.WarehouseLocality", e)
                }
                value={values?.Identity?.Location?.WarehouseLocality}
              />
              {isSubmitted && errors.Identity?.Location?.WarehouseLocality && (
                <div className={Styles.error}>
                  {errors.Identity.Location.WarehouseLocality}
                </div>
              )}
              <br />
              <Select
                label="Label Media Type"
                options={[
                  { label: "Select Label Media Type", value: "default" },
                  { label: "26mm x 26mm", value: "26mm x 26mm" },
                  { label: "51mm x 26mm", value: "51mm x 26mm" },
                  { label: "51mm x 51mm", value: "51mm x 51mm" },
                  { label: "76mm x 26mm", value: "76mm x 26mm" },
                  { label: "76mm x 51mm", value: "76mm x 51mm" },
                  { label: "76mm x 76mm", value: "76mm x 76mm" },
                  { label: "100mm x 26mm", value: "100mm x 26mm" },
                  { label: "100mm x 51mm", value: "100mm x 51mm" },
                  { label: "100mm x 76mm", value: "100mm x 76mm" },
                  { label: "100mm x 100mm", value: "100mm x 100mm" },
                  { label: "100mm x 150mm", value: "100mm x 150mm" }
                ]}
                style={{ textAlign: "left" }}
                name="Configuration.LabelMediaType"
                onChange={(e) =>
                  handleChange("Configuration.LabelMediaType", e)
                }
                value={values?.Configuration?.LabelMediaType}
              />
              {isSubmitted && errors.Configuration?.LabelMediaType && (
                <div className={Styles.error}>
                  {errors.Configuration.LabelMediaType}
                </div>
              )}
              <br />
              <label className={Styles.label}></label>
              <br />
              <Button
                type="submit"
                className={Styles.button}
                disabled={isSaveButtonDisabled}
              >
                Update
              </Button>
            </form>
          </Box>
        )}
      </div>
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        layout="default"
        header={{
          isDismissible: true
        }}
        className={Styles.modal}
      >
        <div className="text-center fw-400">
          <h3 className="text-center fw-500">{apiHeading}</h3>
          <h5 className="fw-400">{apiMessage}</h5>
        </div>
      </Modal>
      <Footerfile />
    </div>
  );
};

export default EditPrinter;
